import React, { useState, useEffect } from 'react';
import { useNetwork } from '@/providers/NetworkProvider';
import { ethers } from 'ethers';
import '@/styles/hacker-aesthetic.css';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

interface ReferralStats {
  referralCode: string;
  totalReferrals: number;
  activeReferrals: number;
  totalEarned: string;
  referralBonus: string;
  hasOfficialCode: boolean;
}

interface ReferralPanelProps {
  onClose: () => void;
}

export const ReferralPanel: React.FC<ReferralPanelProps> = ({ onClose }) => {
  const { contracts, signer, isConnected } = useNetwork();
  const { t } = useTranslation();
  const [referralStats, setReferralStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isConnected && contracts) {
      loadReferralStats();
    }
  }, [isConnected, contracts]);
  const loadReferralStats = async () => {
    if (!signer || !contracts) return;

    try {
      setLoading(true);
      const address = await signer.getAddress();

      // Get real referral stats from contract
      let referralCode = '';
      let totalReferrals = 0;
      let activeReferrals = 0;
      let totalEarned = '0 ETCPOWv2';
      let rawReferralData: any = null;
      let hasOfficialCode = false;
      
      if (contracts.gameLogic) {
        try {
          rawReferralData = await contracts.gameLogic.getReferralStats(address);
          
          // Handle potential UTF-8 decoding errors
          try {
            referralCode = rawReferralData.referralCode || '';
          } catch (decodeError) {
            referralCode = '';
          }
          
          totalReferrals = Number(rawReferralData.totalReferrals || 0);
          activeReferrals = Number(rawReferralData.activeReferrals || 0);
          const totalRewardsEarned =
            rawReferralData.totalRewardsEarned ??
            rawReferralData.totalRewards ??
            0;
          totalEarned = `${ethers.formatEther(totalRewardsEarned)} ETCPOWv2`;
          
          hasOfficialCode = Boolean(referralCode && referralCode.length > 0);
          if (!referralCode || referralCode.length === 0) {
            referralCode = '';
          }
        } catch (error) {
          console.error('Failed to load referral stats from contract:', error);
          referralCode = '';
          hasOfficialCode = false;
        }
      } else {
        referralCode = '';
        hasOfficialCode = false;
      }
      
      setReferralStats({
        referralCode,
        totalReferrals,
        activeReferrals,
        totalEarned,
        referralBonus: '1 ETCPOWv2',
        hasOfficialCode: hasOfficialCode
      });
    } catch (error) {
      console.error('Failed to load referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralStats) {
      navigator.clipboard.writeText(referralStats.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareOnTwitter = () => {
    if (referralStats) {
      const text = `Join Node Commander and use my referral code: ${referralStats.referralCode}!`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
    }
  };

  const shareOnTelegram = () => {
    if (referralStats) {
      const text = `Join Node Commander and use my referral code: ${referralStats.referralCode}!`;
      const url = `https://t.me/share/url?url=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
    }
  };

  const generateReferralCode = async () => {
    if (contracts.gameLogic && signer) {
      try {
        const userAddress = await signer.getAddress();
        toast.loading('Generating referral code...', { id: 'generate' });
        const tx = await contracts.gameLogic.generateReferralCode(userAddress);
        await tx.wait();
        await loadReferralStats();
        toast.success('Referral code generated!', { id: 'generate' });
      } catch (error: any) {
        console.error('Failed to generate referral code:', error);
        toast.error(error.message || 'Failed to generate referral code', { id: 'generate' });
      }
    }
  };

  return (
    <div className="ref-panel">
      <div className="ref-header">
        <div className="ref-heading">
          <div className="ref-title">{t('referral.title')}</div>
          <span className="ref-chip">{t('referral.bonus')}</span>
        </div>
        <button className="ref-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
      </div>

      {loading && (
        <div className="loading-overlay">
          <div className="hacker-loading"></div>
          <span>{t('referral.loading')}</span>
        </div>
      )}

      {!loading && referralStats && (
        <div className="ref-body">
          {/* Hero code display */}
          <div className="ref-hero">
            <span className="ref-hero-label">{t('referral.your_code')}</span>
            <div className="ref-code-row">
              <span className={`ref-code ${referralStats.referralCode ? '' : 'ref-code-empty'}`}>
                {referralStats.referralCode || 'No code generated yet'}
              </span>
              <div className="ref-code-actions">
                {referralStats.referralCode && (
                  <button
                    className={`ref-copy-btn ${copied ? 'copied' : ''}`}
                    onClick={copyReferralCode}
                  >
                    {copied ? t('referral.copied') : t('referral.copy')}
                  </button>
                )}
                {(!referralStats.hasOfficialCode) && (
                  <button
                    className="ref-generate-btn"
                    onClick={generateReferralCode}
                  >
                    {t('referral.generate')}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Referral Stats */}
          <div className="ref-section">
            <div className="ref-section-title">{t('referral.statistics')}</div>
            <div className="ref-stats-grid">
              <div className="ref-stat-card">
                <span className="ref-stat-label">{t('referral.total_referrals')}</span>
                <span className="ref-stat-value">{referralStats.totalReferrals}</span>
              </div>
              <div className="ref-stat-card">
                <span className="ref-stat-label">{t('referral.active_referrals')}</span>
                <span className="ref-stat-value">{referralStats.activeReferrals}</span>
              </div>
              <div className="ref-stat-card">
                <span className="ref-stat-label">{t('referral.total_earned')}</span>
                <span className="ref-stat-value">{referralStats.totalEarned || '0 ETCPOWv2'}</span>
              </div>
              <div className="ref-stat-card">
                <span className="ref-stat-label">{t('referral.referral_bonus')}</span>
                <span className="ref-stat-value">{referralStats.referralBonus}</span>
              </div>
            </div>
          </div>

          {/* Share */}
          <div className="ref-section">
            <div className="ref-section-title">{t('referral.share_code')}</div>
            <div className="ref-share-row">
              <button className="hacker-button" onClick={shareOnTwitter}>
                {t('referral.share_twitter')}
              </button>
              <button className="hacker-button" onClick={shareOnTelegram}>
                {t('referral.share_telegram')}
              </button>
            </div>
          </div>

          {/* How it works */}
          <div className="ref-section">
            <div className="ref-section-title">{t('referral.program_details')}</div>
            <div className="ref-steps">
              <div className="ref-step">
                <span className="ref-step-number">1</span>
                <div className="ref-step-body">
                  <span className="ref-step-label">{t('referral.bonus_rate')}</span>
                  <span className="ref-step-text">{t('referral.bonus_desc')}</span>
                </div>
              </div>
              <div className="ref-step">
                <span className="ref-step-number">2</span>
                <div className="ref-step-body">
                  <span className="ref-step-label">{t('referral.payout')}</span>
                  <span className="ref-step-text">{t('referral.payout_desc')}</span>
                </div>
              </div>
              <div className="ref-step">
                <span className="ref-step-number">3</span>
                <div className="ref-step-body">
                  <span className="ref-step-label">{t('referral.minimum')}</span>
                  <span className="ref-step-text">{t('referral.minimum_desc')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ref-panel {
          background: linear-gradient(170deg, rgba(6, 17, 13, 0.92), rgba(2, 8, 6, 0.97));
          border: 1px solid rgba(0, 255, 136, 0.25);
          border-radius: 16px;
          box-shadow:
            0 30px 60px rgba(0, 0, 0, 0.55),
            inset 0 1px 0 rgba(125, 255, 194, 0.08);
          backdrop-filter: blur(18px);
          width: min(760px, 95vw);
          max-height: min(88vh, 860px);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: relative;
          color: #d8fff1;
          font-family: 'IBM Plex Mono', monospace;
        }

        .ref-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          padding: 18px 22px;
          border-bottom: 1px solid rgba(0, 255, 136, 0.16);
          background: linear-gradient(90deg, rgba(0, 255, 136, 0.06), rgba(20, 184, 166, 0.04));
        }

        .ref-heading {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }

        .ref-title {
          font-family: 'Orbitron', sans-serif;
          font-size: 18px;
          font-weight: 700;
          color: #7dffc2;
          text-transform: uppercase;
          letter-spacing: 3px;
          white-space: nowrap;
        }

        .ref-chip {
          font-size: 11px;
          font-weight: 600;
          color: #7dffc2;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          padding: 5px 12px;
          border: 1px solid rgba(0, 255, 136, 0.28);
          border-radius: 999px;
          background: rgba(0, 255, 136, 0.07);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ref-close {
          flex: 0 0 auto;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid rgba(0, 255, 136, 0.25);
          border-radius: 10px;
          background: rgba(0, 255, 136, 0.05);
          color: #7dffc2;
          font-size: 20px;
          line-height: 1;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .ref-close:hover {
          border-color: rgba(0, 255, 136, 0.55);
          background: rgba(0, 255, 136, 0.12);
          box-shadow: 0 0 14px rgba(0, 255, 136, 0.25);
        }

        .ref-body {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 20px 22px;
          display: flex;
          flex-direction: column;
          gap: 18px;
        }

        .ref-body::-webkit-scrollbar {
          width: 8px;
        }

        .ref-body::-webkit-scrollbar-thumb {
          background: rgba(0, 255, 136, 0.25);
          border-radius: 4px;
        }

        /* Hero code display */
        .ref-hero {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 22px;
          border: 1px solid rgba(0, 255, 136, 0.3);
          border-radius: 14px;
          background:
            radial-gradient(120% 160% at 50% -30%, rgba(0, 255, 136, 0.12), transparent 60%),
            rgba(0, 255, 136, 0.03);
        }

        .ref-hero-label {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          color: rgba(125, 255, 194, 0.55);
        }

        .ref-code-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }

        .ref-code {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 30px;
          font-weight: 700;
          letter-spacing: 0.14em;
          color: #00ff88;
          text-shadow: 0 0 24px rgba(0, 255, 136, 0.35);
          word-break: break-all;
        }

        .ref-code-empty {
          font-size: 15px;
          letter-spacing: 0.04em;
          color: rgba(216, 255, 241, 0.5);
          text-shadow: none;
        }

        .ref-code-actions {
          display: flex;
          gap: 8px;
        }

        .ref-copy-btn,
        .ref-generate-btn {
          font-family: 'IBM Plex Mono', monospace;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          padding: 9px 18px;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.18s ease;
        }

        .ref-copy-btn {
          border: 1px solid rgba(0, 255, 136, 0.35);
          background: rgba(0, 255, 136, 0.08);
          color: #7dffc2;
        }

        .ref-copy-btn:hover {
          border-color: rgba(0, 255, 136, 0.6);
          box-shadow: 0 0 14px rgba(0, 255, 136, 0.25);
        }

        .ref-copy-btn.copied {
          background: #00ff88;
          border-color: #00ff88;
          color: #04140f;
        }

        .ref-generate-btn {
          border: 1px solid rgba(20, 184, 166, 0.5);
          background: rgba(20, 184, 166, 0.1);
          color: #5eead4;
        }

        .ref-generate-btn:hover {
          border-color: rgba(20, 184, 166, 0.8);
          box-shadow: 0 0 14px rgba(20, 184, 166, 0.25);
        }

        /* Sections */
        .ref-section {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .ref-section-title {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.14em;
          color: rgba(125, 255, 194, 0.55);
        }

        .ref-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 10px;
        }

        .ref-stat-card {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 14px;
          border: 1px solid rgba(0, 255, 136, 0.15);
          border-radius: 12px;
          background: rgba(0, 255, 136, 0.03);
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .ref-stat-card:hover {
          border-color: rgba(0, 255, 136, 0.35);
          box-shadow: 0 0 16px rgba(0, 255, 136, 0.1);
        }

        .ref-stat-label {
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: rgba(125, 255, 194, 0.55);
        }

        .ref-stat-value {
          font-family: 'Orbitron', sans-serif;
          font-size: 17px;
          font-weight: 700;
          color: #e8fff5;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .ref-share-row {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
        }

        /* How-it-works steps */
        .ref-steps {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .ref-step {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          padding: 12px 14px;
          border: 1px solid rgba(0, 255, 136, 0.12);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.015);
        }

        .ref-step-number {
          flex: 0 0 auto;
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          border: 1px solid rgba(0, 255, 136, 0.35);
          background: rgba(0, 255, 136, 0.08);
          color: #00ff88;
          font-family: 'Orbitron', sans-serif;
          font-size: 12px;
          font-weight: 700;
        }

        .ref-step-body {
          display: flex;
          flex-direction: column;
          gap: 3px;
          min-width: 0;
        }

        .ref-step-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          color: #7dffc2;
        }

        .ref-step-text {
          font-size: 12px;
          line-height: 1.5;
          color: rgba(216, 255, 241, 0.65);
        }

        @media (max-width: 640px) {
          .ref-title {
            font-size: 15px;
            letter-spacing: 2px;
          }

          .ref-code {
            font-size: 22px;
          }

          .ref-body {
            padding: 14px 16px;
          }
        }
      `}</style>
    </div>
  );
};
